// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import saveShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SaveShipping";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import updateShipTo from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import BillToSelectorModal, {
    BillToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import ShippingAddressForm, {
    ShippingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import ShipToSelectorModal, {
    ShipToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import xngGaCheckoutStepsEvent from "@sc/App/GoogleAnalyticsConnector/Handlers/Overrides/Checkout/CheckoutStepsEvent";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { GOOGLE_EVENTS, SHIPPING_FORM } from "@sc/XDL/constants";
import { parseBoolean, xValidateOnSubmit } from "@sc/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

// XNG-Changes: TODO - OneTimeAddress is customized in this widget

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;
    const session = getSession(state);

    // XNG-Changes
    let oneTimeAddress;
    const shipTosDataView = cart
        ? getShipTosDataView(state, {
              billToId: cart.billToId,
              expand: ["validation", "properties"] as any,
              exclude: ["showAll"],
          })
        : undefined;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
    }

    let newAddress;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
    }
    return {
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isSavingShipping: state.pages.onePageCheckout.isSavingShipping,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        shipTosDataView,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod: cart?.isAwaitingApproval ? cart.fulfillmentMethod : session.fulfillmentMethod,
        pickUpWarehouse: session.pickUpWarehouse,
        newAddress,
        updateCartInsteadOfContext: cart?.isAwaitingApproval,
        // XNG-Change
        customerSettings: getSettingsCollection(state).customerSettings,
        collectCustomerAddress: parseBoolean(session?.properties?.collectCustomAddress || "false"),
        useOneTimeAddress: (state as CustomAppState).xCheckout.useOneTimeAddress,
        isUpdatingOneTimeAddress: (state as CustomAppState).xCheckout.isUpdatingOneTimeAddress,
        oneTimeAddress,
        isUpdatingCustomer: state.pages.onePageCheckout.isUpdatingCustomer,
        // xng-change
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
    // XNG-Change
    updateShipTo,
    // xng-change
    xngGaCheckoutStepsEvent,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface OnePageCheckoutShippingSectionStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    useOneTimeAddressGridItem?: GridItemProps;
    useOneTimeAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    // XNG-Change
    shipToNote?: SmallHeadingAndTextStyles;
    wrapper?: InjectableCss;
    addressWrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    billToSectionWrapper?: InjectableCss;
    billToWrapper?: InjectableCss;
    addressTextStyle?: TypographyPresentationProps;
    pickupSectionWrapper?: InjectableCss;
    pickupWrapper?: InjectableCss;
    fieldWrapper?: InjectableCss;
}

export const onePageCheckoutShippingSectionStyles: OnePageCheckoutShippingSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: { weight: 600 },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    useOneTimeAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = onePageCheckoutShippingSectionStyles;

const OnePageCheckoutShippingSection = ({
    isShippingAddressUpdateRequired,
    isBillingAddressUpdateRequired,
    isBillingAddressTheSameAsShipping,
    isSavingShipping,
    cart,
    shipToState,
    billToState,
    currentUserIsGuest,
    countries,
    shipTosDataView,
    shipToAddressFields,
    billToAddressFields,
    fulfillmentMethod,
    pickUpWarehouse,
    newAddress,
    theme,
    updatePickUpWarehouse,
    updatePickUpWarehouseForCart,
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    saveShipping,
    setPaymentDetailsState,
    updateCartInsteadOfContext,
    // XNG-Change
    customerSettings,
    collectCustomerAddress,
    useOneTimeAddress,
    isUpdatingOneTimeAddress,
    updateShipTo,
    oneTimeAddress,
    isUpdatingCustomer,
    // xng-change
    xGoogleAnalytics,
    xngGaCheckoutStepsEvent,
}: Props) => {
    // XNG-Change if customer has collectCustomerAddress, then we need to display onetimeaddress form (either on pickup or shipto)
    const context = useContext(OnePageCheckoutFormContext);
    const [isEditing, setIsEditing] = useState(false);
    const [isShipToSelectorModalOpen, setIsShipToSelectorModalOpen] = useState(false);
    const [isBillToSelectorModalOpen, setIsBillToSelectorModalOpen] = useState(false);
    // XNG-Change
    const [isFindLocationOpen, setIsFindLocationOpen] = useState(false);
    const [smid, setSmid] = useState(oneTimeAddress?.properties?.smid || "");
    const [phone2, setPhone2] = useState(oneTimeAddress?.properties?.phone2 || "");
    const [companyDepartmentNumber, setCompanyDepartmentNumber] = useState(
        oneTimeAddress?.properties?.companyDepartmentNumber || "",
    );

    const toasterContext = useContext(ToasterContext);
    const dispatch = useAppDispatch();

    // XNG-Change
    useEffect(() => {
        if (
            !cart?.isAwaitingApproval &&
            collectCustomerAddress &&
            oneTimeAddress &&
            shipToState &&
            shipToState.value &&
            countries &&
            !useOneTimeAddress &&
            !isUpdatingCustomer
        ) {
            updateShipTo({
                billToId: cart?.billToId || "",
                shipTo: oneTimeAddress,
                isEdit: true,
                onSuccess: () => {
                    setIsEditing(true);
                    dispatch({
                        type: "xCheckout/Update",
                        data: {
                            useOneTimeAddress: true,
                        },
                    } as any);
                    dispatch({
                        type: "xCheckout/SetIsUpdatingOneTimeAddress",
                        isUpdatingOneTimeAddress: true,
                    });
                },
            });
        }
    }, [collectCustomerAddress, oneTimeAddress, shipToState, countries]);

    useEffect(() => {
        if (
            (isShippingAddressUpdateRequired || isBillingAddressUpdateRequired) &&
            (shipToState.value || (cart && cart.shipToId === undefined))
        ) {
            setIsEditing(true);
        }
    }, [isShippingAddressUpdateRequired, isBillingAddressUpdateRequired, shipToState.value, cart]);

    // XNG-Change
    useEffect(() => {
        oneTimeAddress?.properties?.smid && setSmid(oneTimeAddress?.properties?.smid);
        oneTimeAddress?.properties?.companyDepartmentNumber &&
            setCompanyDepartmentNumber(oneTimeAddress?.properties?.companyDepartmentNumber);

        oneTimeAddress?.properties?.phone2 && setPhone2(oneTimeAddress?.properties?.phone2);
    }, [oneTimeAddress?.properties]);

    const shipTo = shipToState.value;
    const billTo = billToState.value;

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation", "properties"] as any, exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId, expand: ["properties"] as any });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    useEffect(() => {
        if (isUpdatingOneTimeAddress && !isEditing) {
            setIsEditing(true);
        }
    }, [isUpdatingOneTimeAddress]);

    // xng-change
    useEffect(() => {
        xngGaCheckoutStepsEvent({
            checkoutEventType: GOOGLE_EVENTS.BEGIN_CHECKOUT,
            includeCheckoutEvent: xGoogleAnalytics.checkoutFlowEvents,
        });

        xngGaCheckoutStepsEvent({
            checkoutEventType: GOOGLE_EVENTS.ADD_SHIPPING_INFO,
            includeCheckoutEvent: xGoogleAnalytics?.checkoutFlowEvents,
        });
    }, []);

    if (!cart) {
        return (
            <StyledWrapper {...styles.loadingWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!countries || !billTo || (!shipTo && !newAddress)) {
        return null;
    }

    const useShippingAddressChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setIsBillingAddressTheSameAsShipping({ isBillingAddressTheSameAsShipping: value });
    };

    const editShippingAddressClickHandler = () => {
        // XNG-Changes: no need since we have customized the onetimeaddress
        // if (useOneTimeAddress) {
        //     setIsEditing(true);
        //     return;
        // }

        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsShipToSelectorModalOpen(true);
    };

    // XNG-Change
    const editOneTimeAddressClickHandler = () => {
        setIsEditing(true);
        dispatch({
            type: "xCheckout/SetIsUpdatingOneTimeAddress",
            isUpdatingOneTimeAddress: true,
        });
    };

    const shipToSelectorModalCloseHandler = () => {
        setIsShipToSelectorModalOpen(false);
    };

    const editBillingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsBillToSelectorModalOpen(true);
    };

    const billToSelectorModalCloseHandler = () => {
        setIsBillToSelectorModalOpen(false);
    };

    const editPickUpWarehouseClickHandler = () => {
        // XNG-Change: if overlay if not enabled, display modal
        if (theme.storePickup?.useOverlay) {
            setStorePickupOverlayIsOpen({ isOpen: true });
            return;
        }
        setIsFindLocationOpen(true);
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        handlePickUpAddressChange(warehouse);
        setIsFindLocationOpen(false);
    };

    const titleEditClickHandler = () => {
        if (currentUserIsGuest) {
            setIsEditing(true);
        } else if (fulfillmentMethod === FulfillmentMethod.PickUp) {
            theme.addressBook?.useOverlay
                ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
                : setIsBillToSelectorModalOpen(true);
        }
    };

    const saveClickHandler = async () => {
        context.formSubmitAttempted = true;

        const isShippingValid = useOneTimeAddress ? xValidateOnSubmit(SHIPPING_FORM).length === 0 : true;

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o) && isShippingValid) {
            saveShipping({
                useOneTimeAddress,
                phone2,
                smid,
                companyDepartmentNumber,
                onSuccess: () => {
                    setIsEditing(false);
                    // XNG-Changes: no need since we have customized the onetimeaddress
                    // context.formSubmitAttempted = false;
                    setPaymentDetailsState({ paymentDetailsState: "active" });
                    dispatch({
                        type: "xCheckout/SetIsUpdatingOneTimeAddress",
                        isUpdatingOneTimeAddress: false,
                    });
                    // xng-change
                    xngGaCheckoutStepsEvent({
                        checkoutEventType: GOOGLE_EVENTS.ADD_SHIPPING_INFO,
                        includeCheckoutEvent: xGoogleAnalytics?.checkoutFlowEvents,
                    });
                },
            } as any);
        } else {
            scrollToFirstFormError();
        }
    };

    // XNG-Change: changes on structure to match design
    return (
        <LoadingOverlay loading={isSavingShipping || isUpdatingCustomer}>
            <StyledWrapper {...styles.wrapper} data-test-selector="shippingAndBillingSection">
                {/* <GridItem {...styles.titleGridItem}>
                    <Typography as="h2" {...styles.titleText}>
                        {fulfillmentMethod === FulfillmentMethod.Ship
                            ? translate("Shipping")
                            : translate("Billing Address")}
                    </Typography>
                </GridItem> */}

                {/* XNG-Change */}
                <Typography {...styles.titleText}>{translate("Addresses")}</Typography>
                {isEditing && (
                    <>
                        {fulfillmentMethod === FulfillmentMethod.Ship && !useOneTimeAddress && (
                            <StyledWrapper {...styles.useShippingAddressGridItem}>
                                <Checkbox
                                    {...styles.useShippingAddressCheckbox}
                                    onChange={useShippingAddressChangeHandler}
                                    checked={isBillingAddressTheSameAsShipping}
                                    data-test-selector="useShippingAddressCheckbox"
                                >
                                    {translate("Billing address is the same as shipping")}
                                </Checkbox>
                            </StyledWrapper>
                        )}
                        {!useOneTimeAddress &&
                            billToAddressFields &&
                            (!isBillingAddressTheSameAsShipping || fulfillmentMethod === FulfillmentMethod.PickUp) && (
                                <StyledWrapper {...styles.sectionWrapper}>
                                    <StyledWrapper {...styles.billToGridItem}>
                                        <BillingAddressForm
                                            countries={countries}
                                            fieldDisplay={billToAddressFields}
                                            extendedStyles={styles.billingAddressForm}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        {useOneTimeAddress && billTo && (
                            <StyledWrapper {...styles.billToSectionWrapper}>
                                <StyledWrapper {...styles.billToWrapper}>
                                    <Typography {...styles.heading}>{translate("Bill-To Account")}</Typography>
                                    {!currentUserIsGuest &&
                                        customerSettings.allowBillToAddressEdit &&
                                        cart.status !== "QuoteProposed" &&
                                        !(cart.status === "Cart" && cart.type === "Job") && (
                                            <>
                                                <Link
                                                    {...styles.editLink}
                                                    onClick={editBillingAddressClickHandler}
                                                    data-test-selector="editBillingAddressButton"
                                                    aria-label={translate("Change Bill-To Account")}
                                                >
                                                    {translate("Change Bill-To Account")}
                                                    <VisuallyHidden>{translate("Billing Address")}</VisuallyHidden>
                                                </Link>
                                                <BillToSelectorModal
                                                    isOpen={isBillToSelectorModalOpen}
                                                    onModalClose={billToSelectorModalCloseHandler}
                                                    extendedStyles={styles.billToSelectorModal}
                                                />
                                            </>
                                        )}
                                    <StyledWrapper {...styles.addressWrapper} data-test-selector="BillingAddress">
                                        <AddressInfoDisplay
                                            {...billTo}
                                            {...{ customerSequence: billTo.customerSequence || billTo.customerNumber }}
                                            state={billTo.state ? billTo.state.abbreviation : undefined}
                                            country={billTo.country ? billTo.country.abbreviation : undefined}
                                            extendedStyles={styles.addressInfoDisplay}
                                            // XNG-Changes
                                            {...{ preventTranslation: true }}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        {shipToAddressFields && (fulfillmentMethod === FulfillmentMethod.Ship || useOneTimeAddress) && (
                            <StyledWrapper {...styles.sectionWrapper}>
                                {useOneTimeAddress ? (
                                    <Typography {...styles.heading}>{translate("One-Time Address")}</Typography>
                                ) : (
                                    <Typography {...styles.heading}>{translate("Ship-To Account")}</Typography>
                                )}

                                <StyledWrapper {...styles.shipToGridItem}>
                                    <ShippingAddressForm
                                        countries={countries}
                                        fieldDisplay={shipToAddressFields}
                                        extendedStyles={styles.shippingAddressForm}
                                        // XNG-Changes
                                        {...{ formDisabled: isBillingAddressTheSameAsShipping }}
                                    />

                                    <StyledWrapper {...styles.fieldWrapper}>
                                        <TextField
                                            placeholder={translate("Phone2")}
                                            name="phone2"
                                            label={translate("Phone2")}
                                            value={phone2}
                                            required={true}
                                            onChange={e => setPhone2(e.currentTarget.value)}
                                            data-test-selector="customerAddressForm_phone2"
                                            type="phone"
                                            {...{ formId: SHIPPING_FORM }}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.fieldWrapper}>
                                        <TextField
                                            placeholder={translate("SMID")}
                                            name="smid"
                                            label={translate("SMID")}
                                            value={smid}
                                            required={true}
                                            onChange={e => setSmid(e.currentTarget.value)}
                                            data-test-selector="customerAddressForm_smid"
                                            type="text"
                                            {...{ formId: SHIPPING_FORM }}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.fieldWrapper}>
                                        <TextField
                                            placeholder={translate("Co/Dept Number")}
                                            name="companyDepartmentNumber"
                                            label={translate("Co/Dept Number")}
                                            value={companyDepartmentNumber}
                                            required={true}
                                            data-test-selector="customerAddressForm_deptNumber"
                                            type="text"
                                            onChange={e => setCompanyDepartmentNumber(e.currentTarget.value)}
                                            {...{ formId: SHIPPING_FORM }}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                {useOneTimeAddress && (
                                    <StyledWrapper {...styles.saveButtonGridItem}>
                                        <Button
                                            {...styles.saveButton}
                                            onClick={saveClickHandler}
                                            data-test-selector="saveAndContinueButton"
                                            aria-label={translate("Save & Continue")}
                                        >
                                            {translate("Save & Continue")}
                                        </Button>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        )}
                        {!useOneTimeAddress && (
                            <StyledWrapper {...styles.saveButtonGridItem}>
                                <Button
                                    {...styles.saveButton}
                                    onClick={saveClickHandler}
                                    data-test-selector="saveAndContinueButton"
                                    aria-label={translate("Save & Continue")}
                                >
                                    {translate("Save & Continue")}
                                </Button>
                            </StyledWrapper>
                        )}
                    </>
                )}
                {!isEditing && (
                    <>
                        {/* {(currentUserIsGuest || fulfillmentMethod === FulfillmentMethod.PickUp) && (
                            <StyledWrapper {...styles.titleEditGridItem}>
                                <Link
                                    {...styles.titleEditLink}
                                    onClick={titleEditClickHandler}
                                    data-test-selector="editPickupShippingAddressButton"
                                >
                                    {translate("Edit")}
                                </Link>
                            </StyledWrapper>
                        )} */}
                        {billTo && (
                            <StyledWrapper {...styles.sectionWrapper}>
                                <Typography {...styles.heading}>{translate("Bill-To Account")}</Typography>
                                {!currentUserIsGuest &&
                                    customerSettings.allowBillToAddressEdit &&
                                    cart.status !== "QuoteProposed" &&
                                    !(cart.status === "Cart" && cart.type === "Job") && (
                                        <>
                                            <Link
                                                {...styles.editLink}
                                                onClick={editBillingAddressClickHandler}
                                                data-test-selector="editBillingAddressButton"
                                                aria-label={translate("Change Bill-To Account")}
                                            >
                                                {translate("Change Bill-To Account")}
                                                <VisuallyHidden>{translate("Billing Address")}</VisuallyHidden>
                                            </Link>
                                            <BillToSelectorModal
                                                isOpen={isBillToSelectorModalOpen}
                                                onModalClose={billToSelectorModalCloseHandler}
                                                extendedStyles={styles.billToSelectorModal}
                                            />
                                        </>
                                    )}
                                <StyledWrapper {...styles.addressWrapper} data-test-selector="BillingAddress">
                                    <AddressInfoDisplay
                                        {...billTo}
                                        {...{ customerSequence: billTo.customerSequence || billTo.customerNumber }}
                                        state={billTo.state ? billTo.state.abbreviation : undefined}
                                        country={billTo.country ? billTo.country.abbreviation : undefined}
                                        extendedStyles={styles.addressInfoDisplay}
                                        // XNG-Changes
                                        {...{ preventTranslation: true }}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        {/* XNG-Change: will display shipping address whether the fulfillmentMethod is pickup or shipto */}
                        {(shipTo || newAddress) && (
                            <>
                                <StyledWrapper {...styles.sectionWrapper}>
                                    {useOneTimeAddress ? (
                                        <Typography {...styles.heading}>{translate("One-Time Address")}</Typography>
                                    ) : (
                                        <Typography {...styles.heading}>{translate("Ship-To Account")}</Typography>
                                    )}
                                    {!currentUserIsGuest && !useOneTimeAddress && (
                                        <>
                                            <Link
                                                {...styles.editLink}
                                                onClick={editShippingAddressClickHandler}
                                                data-test-selector="editShippingAddressButton"
                                                aria-label={translate("Change Ship-To Account")}
                                            >
                                                {translate("Change Ship-To Account")}
                                                <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                                            </Link>
                                            <ShipToSelectorModal
                                                isOpen={isShipToSelectorModalOpen}
                                                onModalClose={shipToSelectorModalCloseHandler}
                                                extendedStyles={styles.shipToSelectorModal}
                                            />
                                        </>
                                    )}
                                    {useOneTimeAddress && (
                                        <>
                                            <Link
                                                {...styles.editLink}
                                                onClick={editOneTimeAddressClickHandler}
                                                data-test-selector="editShippingAddressButton"
                                                aria-label={translate("Edit One time address")}
                                            >
                                                {translate("Edit One-Time Address")}
                                                <VisuallyHidden>{translate("Edit One-Time Address")}</VisuallyHidden>
                                            </Link>
                                        </>
                                    )}
                                    <StyledWrapper {...styles.addressWrapper} data-test-selector="ShippingAddress">
                                        {shipTo && (
                                            <AddressInfoDisplay
                                                {...shipTo}
                                                {...{
                                                    customerSequence: useOneTimeAddress
                                                        ? ""
                                                        : shipTo.customerSequence || shipTo.customerNumber,
                                                }}
                                                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                                                country={shipTo.country ? shipTo.country.abbreviation : undefined}
                                                extendedStyles={styles.addressInfoDisplay}
                                                // XNG-Changes
                                                {...{ preventTranslation: true }}
                                            />
                                        )}
                                        {useOneTimeAddress && (
                                            <>
                                                {phone2 && (
                                                    <Typography {...styles.addressTextStyle} variant="p">
                                                        {phone2}
                                                    </Typography>
                                                )}
                                                {smid && (
                                                    <Typography {...styles.addressTextStyle} variant="p">
                                                        {translate("SMID")}: {smid}
                                                    </Typography>
                                                )}
                                                {companyDepartmentNumber && (
                                                    <Typography {...styles.addressTextStyle} variant="p">
                                                        {translate("Co/Dept Number")}: {companyDepartmentNumber}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                        {!useOneTimeAddress && (
                                            <SmallHeadingAndText
                                                extendedStyles={styles.shipToNote}
                                                heading={translate("Note")}
                                                text={`${siteMessage("Custom_Ship_To_Incorrect_Message")}`}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        )}
                    </>
                )}
                {fulfillmentMethod === FulfillmentMethod.PickUp && pickUpWarehouse && (
                    <StyledWrapper {...styles.pickupSectionWrapper}>
                        <StyledWrapper {...styles.pickupWrapper} {...{ fullHeight: !isUpdatingOneTimeAddress }}>
                            <Typography {...styles.heading}>{translate("Pick Up Address")}</Typography>
                            {!cart.isAwaitingApproval && (
                                <Link
                                    {...styles.editLink}
                                    aria-label={translate("Change Branch")}
                                    onClick={editPickUpWarehouseClickHandler}
                                >
                                    {/* XNG-Change */}
                                    {translate("Change Branch")}
                                </Link>
                            )}
                            <StyledWrapper {...styles.addressWrapper}>
                                <AddressInfoDisplay
                                    {...pickUpWarehouse}
                                    companyName={pickUpWarehouse.description || pickUpWarehouse.name}
                                    extendedStyles={styles.addressInfoDisplay}
                                />
                            </StyledWrapper>
                            {/* XNG-Change */}
                            <FindLocationModal
                                modalIsOpen={isFindLocationOpen}
                                onWarehouseSelected={handleWarehouseSelected}
                                onModalClose={handleFindLocationModalClose}
                                forCart
                                extendedStyles={styles.findLocationModal}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                {/* XNG-Change: Add Order details section */}
                {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                    fields: {},
                })}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutShippingSection)),
    definition: {
        displayName: "Shipping Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
